/**
 * Cookie Consent Banner
 * Shown once on first visit; stores consent in localStorage.
 * GDPR/ePrivacy compliant — blocks until user accepts or declines.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'cookie_consent';

type ConsentState = 'accepted' | 'declined' | null;

function getStoredConsent(): ConsentState {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'accepted' || v === 'declined') return v;
  } catch {
    // localStorage may be blocked
  }
  return null;
}

function setStoredConsent(value: 'accepted' | 'declined'): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only if no decision has been made yet
    if (getStoredConsent() === null) {
      // Small delay so it doesn't flash on initial paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAccept = () => {
    setStoredConsent('accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    setStoredConsent('declined');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1 text-sm text-gray-700">
          <span className="font-medium">We use cookies</span> to keep you logged in and
          analyze anonymous usage. We don't sell or share your data with advertisers.{' '}
          <Link to="/privacy" className="text-blue-600 hover:underline">
            Privacy Policy
          </Link>
          {' · '}
          <Link to="/terms" className="text-blue-600 hover:underline">
            Terms of Service
          </Link>
          {' · '}
          <Link to="/host-agreement" className="text-blue-600 hover:underline">
            Host Agreement
          </Link>
        </div>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={handleDecline}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

/** Check if the user has accepted cookies (for analytics gating) */
export function hasCookieConsent(): boolean {
  return getStoredConsent() === 'accepted';
}
